<template>
  <div id="app" class="bg-surface font-body text-on-surface">
    <header
      v-if="isHeaderVisible"
      :class="['fixed top-0 w-full z-50 glass-header shadow-sm', { 'portal-home-header-2x': useLargePortalHeader }]"
    >
      <div class="portal-shrink-header-shell flex justify-between items-center w-full px-8 py-4 max-w-[1440px] mx-auto gap-4 overflow-x-auto no-scrollbar">
        <div class="flex items-center gap-8 shrink-0">
          <div class="text-4xl font-bold tracking-tight text-primary font-headline whitespace-nowrap"><br></div>
          <nav class="flex items-center gap-6 whitespace-nowrap">
            <a :class="navLinkClasses('home')" href="#" @click.prevent="goSection('home')">首页</a>
            <a :class="navLinkClasses('about')" href="#" @click.prevent="goSection('about')">关于我们</a>
            <a :class="navLinkClasses('news')" href="#" @click.prevent="goSection('news')">新闻中心</a>
            <a :class="navLinkClasses('experts')" href="#" @click.prevent="goSection('experts')">专家智库</a>
            <a :class="navLinkClasses('support')" href="#" @click.prevent="goSection('support')">智力支持</a>
            <a :class="navLinkClasses('case-center')" href="#" @click.prevent="goSection('case-center')">案例中心</a>
            <a :class="navLinkClasses('database')" href="#" @click.prevent="goSection('database')">创新成果和管理论文数据库</a>
            <a :class="navLinkClasses('journal')" href="#" @click.prevent="goSection('journal')">期刊</a>
            <a :class="navLinkClasses('contact-us')" href="#" @click.prevent="goSection('contact-us')">联系我们</a>
          </nav>
        </div>
        <div class="flex items-center gap-6 shrink-0">
          <div class="flex items-center gap-3">
            <button
              class="portal-shrink-action-button bg-primary text-on-primary px-6 py-3 rounded-lg text-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap shadow-sm"
              type="button"
              @click="goLogin"
            >
              创新成果与论文申报入口
            </button>
            <button
              class="portal-shrink-action-button bg-secondary-container text-on-secondary-container px-6 py-3 rounded-lg text-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap shadow-sm"
              type="button"
              @click="goExpertPortal"
            >
              专家智库入口
            </button>
          </div>
          <div class="flex gap-3 text-primary">
            <span class="material-symbols-outlined cursor-pointer hover:text-secondary transition-colors" title="搜索" @click="goComingSoon">search</span>
          </div>
        </div>
      </div>
    </header>

    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App',
  computed: {
    isHomeRoute () {
      return this.$route.path === '/home' || this.$route.path === '/'
    },
    isNewsRoute () {
      return this.$route.path === '/content/list' || this.$route.path.startsWith('/content/')
    },
    isAboutRoute () {
      return this.$route.path.startsWith('/about/')
    },
    isSupportRoute () {
      return this.$route.path.startsWith('/support')
    },
    isDatabaseRoute () {
      return this.$route.path === '/database' || this.$route.path.startsWith('/database/innovations') || this.$route.path.startsWith('/database/thesis')
    },
    isCaseRoute () {
      return this.$route.path === '/case-center' || this.$route.path.startsWith('/case-center/')
    },
    isJournalRoute () {
      return this.$route.path === '/journal' || this.$route.path.startsWith('/journal/')
    },
    isPartnerRoute () {
      return this.$route.path.startsWith('/partner/')
    },
    isComingSoonRoute () {
      return this.$route.path === '/coming-soon'
    },
    useLargePortalHeader () {
      return this.isHomeRoute || this.isAboutRoute || this.isNewsRoute || this.isSupportRoute || this.isDatabaseRoute || this.isCaseRoute || this.isJournalRoute || this.isPartnerRoute || this.isComingSoonRoute
    },
    isHeaderVisible () {
      const path = this.$route.path
      return !path.startsWith('/contest') && !path.startsWith('/expert') && path !== '/declare/login'
    },
    headerActiveSection () {
      if (this.isHomeRoute) {
        return this.activeSection
      }
      if (this.isNewsRoute) {
        return 'news'
      }
      if (this.isAboutRoute) {
        return 'about'
      }
      if (this.isSupportRoute) {
        return 'support'
      }
      if (this.isDatabaseRoute) {
        return 'database'
      }
      if (this.isCaseRoute) {
        return 'case-center'
      }
      if (this.isJournalRoute) {
        return 'journal'
      }
      if (this.isPartnerRoute) {
        return 'journal'
      }
      return ''
    }
  },
  data () {
    return {
      activeSection: 'home',
      sectionIds: ['home', 'about', 'news', 'experts', 'support', 'case-center', 'database', 'journal', 'contact-us'],
      scrollHandler: null
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateScrollSpyState()
    })
  },
  beforeDestroy () {
    this.teardownScrollSpy()
  },
  watch: {
    $route: {
      immediate: true,
      handler () {
        this.syncActiveSection()
        this.$nextTick(() => {
          this.updateScrollSpyState()
        })
      }
    }
  },
  methods: {
    navLinkClasses (id) {
      if (this.headerActiveSection === id) {
        return 'nav-link-reset text-primary font-bold border-b-2 border-secondary pb-1 transition-transform duration-200 -translate-y-0.5 text-lg font-semibold no-underline hover:no-underline'
      }

      return 'nav-link-reset text-slate-600 hover:text-secondary transition-colors duration-200 text-lg font-semibold no-underline hover:no-underline'
    },
    syncActiveSection () {
      if (this.$route.path !== '/home' && this.$route.path !== '/') {
        this.activeSection = ''
        return
      }

      this.activeSection = this.$route.query.section || 'home'
    },
    updateScrollSpyState () {
      if (!this.isHomeRoute) {
        this.teardownScrollSpy()
        return
      }

      if (!this.scrollHandler) {
        this.scrollHandler = this.handleScroll.bind(this)
        window.addEventListener('scroll', this.scrollHandler, { passive: true })
      }

      this.handleScroll()
    },
    teardownScrollSpy () {
      if (!this.scrollHandler) {
        return
      }

      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    },
    handleScroll () {
      if (!this.isHomeRoute) {
        return
      }

      const scrollTop = window.pageYOffset || window.scrollY || 0
      const activationOffset = 260
      let currentSection = 'home'

      this.sectionIds.forEach((id) => {
        const el = document.getElementById(id)
        if (el && scrollTop + activationOffset >= el.offsetTop) {
          currentSection = id
        }
      })

      this.activeSection = currentSection
    },
    goLogin () {
      this.$router.push({
        path: '/declare/login',
        query: {
          source: 'homepage',
          returnUrl: '/#/home'
        }
      })
    },
    goExpertPortal () {
      const target = (process.env.EXPERT_PORTAL_URL || '/expert/').replace(/\/$/, '') || '/expert'
      if (/^https?:\/\//.test(target) || target.startsWith('//')) {
        window.location.href = target
        return
      }
      this.$router.push(target)
    },
    goSection (id) {
      this.activeSection = id

      if (!this.isHomeRoute) {
        if (id === 'case-center') {
          this.$router.push('/case-center')
          return
        }
        this.$router.push({ path: '/home', query: { section: id } })
        return
      }

      this.$nextTick(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      })
    },
    goComingSoon () {
      this.$router.push('/coming-soon')
    },
  }
}

</script>

<style scoped>
.nav-link-reset,
.nav-link-reset:hover,
.nav-link-reset:focus,
.nav-link-reset:active,
.nav-link-reset:visited {
  text-decoration: none !important;
  -webkit-text-decoration: none !important;
  outline: none !important;
  box-shadow: none !important;
  -webkit-tap-highlight-color: transparent;
}

.nav-link-reset:focus-visible {
  outline: 2px solid rgba(197, 160, 89, 0.45) !important;
  outline-offset: 4px;
  border-radius: 6px;
}
</style>



// WEBPACK FOOTER //
// src/App.vue